import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import "../../Components/Course/ParticularCourse.scss";
import CourseOverView from "../../Components/Course/CourseOverView";
import CourseSpStudyNotes from "../../Components/Course/CourseSpStudyNotes";
import CoursePracPaper from "../../Components/Course/CoursePracPaper";
import CourseQuestionBank from "../../Components/Course/CourseQuestionBank";

import CourseReview from "../../Components/Course/CourseReview";
import CourseFaq from "../../Components/Course/CourseFaq";
import CourseBanner from "../../Components/Course/CourseBanner";
import CourseBenefits from "../../Components/Course/CourseBenefits";
import ScrollTop from "../../Components/Buttons/ScrollTop";
import { getCourseBySlug } from "../../services/getCourseBySlug";
import { GoChevronRight } from "react-icons/go";
import { toast } from "react-toastify";

const ParticularCourse = () => {
  const location = useLocation();
  const state = location.state;
  const [activeTab, setActiveTab] = useState("3");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 425);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 425);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const { universitySlug, courseSlug } = useParams();
  const [data, setData] = useState({});
  const tabRefs = {
    1: useRef(null),
    2: useRef(null),
    3: useRef(null),
    4: useRef(null),
    5: useRef(null),
    6: useRef(null),
  };
  const fetchData = async () => {
    try {
      const response = await getCourseBySlug(universitySlug, courseSlug);
      if (response.isSuccess) {
        setData(response?.data);
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error("Failed to fetch courses");
    }
  };

  useEffect(() => {
    if (state?.id) {
      handleTabClick(state.id);
    } else {
      handleTabClick("3");
    }
    fetchData();
  }, [universitySlug, courseSlug]);

  const handleTabClick = (id) => {
    setActiveTab(id);
  };

  /* 
  ++++++++++++++++++++++++++++++++++NOTE++++++++++++++++++++++++++++++++++++++++
  Whenever you change the id of the tabs, make sure to change the id in every components of 
  Special Study Notes, Practice Papers, Question Bank  as well.......
  */
  const tabs = [
    { id: "1", label: "Overview" },
    { id: "2", label: "Special Study Notes" },
    { id: "3", label: "Practice Papers" },
    { id: "4", label: "Question Bank" },
    // { "id": "5", "label": "Combined Package" },
    { id: "5", label: "Reviews" },
    { id: "6", label: "FAQ's" },
  ];

  const activeTabLabel = tabs.find((tab) => tab.id === activeTab)?.label || "";
  return (
    <>
      <CourseBanner data={data} onReviewsClick={() => handleTabClick("5")} />
      <div className="container my-5">
        <div className="subject-tabs-div mb-5">
          <div className="row border">
            <div className="col-lg-3 col-md-4 px-0">
              <ul className="ft-link nav-tabs list-unstyled m-0">
                {tabs.map((tab) => (
                  <li key={tab.id} className={`nav-item`}>
                    <div
                      className={`d-flex justify-content-between align-items-center px-3 py-3 ${
                        activeTab === tab.id ? "active" : ""
                      }`}
                      style={{ cursor: "pointer" }}
                      onClick={() => {
                        if (isMobile) {
                          const newActiveTab =
                            activeTab === tab.id ? "" : tab.id;
                          setActiveTab(newActiveTab);
                          setTimeout(() => {
                            if (
                              newActiveTab &&
                              tabRefs[newActiveTab]?.current
                            ) {
                              tabRefs[newActiveTab].current.scrollIntoView({
                                behavior: "smooth",
                                block: "start",
                              });
                            }
                          }, 100);
                        } else {
                          handleTabClick(tab.id);
                        }
                      }}
                    >
                      <div className="d-flex align-items-center gap-3">
                        {tab.label}
                      </div>

                      <GoChevronRight
                        size={22}
                        style={{
                          transition: "0.3s",
                          transform: isMobile
                            ? activeTab === tab.id
                              ? "rotate(90deg)"
                              : "rotate(0deg)"
                            : "rotate(0deg)",
                        }}
                      />
                    </div>

                    {isMobile && activeTab === tab.id && (
                      <div
                        className="px-3 pb-4 mt-5"
                        style={{ color: "#333" }}
                        ref={tabRefs[tab.id]}
                      >
                        {tab.id === "1" && (
                          <CourseOverView overviewData={data?.overview} />
                        )}

                        {tab.id === "2" && (
                          <CourseSpStudyNotes
                            Link={data?.studyNotes}
                            course={data}
                            id="spsn"
                          />
                        )}

                        {tab.id === "3" && (
                          <CoursePracPaper
                            Link={data?.practicePapers}
                            course={data}
                            id="pp"
                          />
                        )}

                        {tab.id === "4" && (
                          <CourseQuestionBank
                            Link={data?.questionBank}
                            course={data}
                            id="qb"
                          />
                        )}

                        {tab.id === "5" && (
                          <CourseReview
                            courseID={data?._id}
                            avgRatings={data?.ratings}
                          />
                        )}

                        {tab.id === "6" && <CourseFaq />}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            {!isMobile && (
              <div className="col-lg-9 col-md-8">
                <div
                  className="row all-alphabet ps-4 pt-4 "
                  style={{
                    width: "100%",
                    boxSizing: "border-box",
                    whiteSpace: "normal",
                    wordWrap: "break-word",
                    overflowWrap: "break-word",
                    padding: "0 1rem",
                  }}
                >
                  <h2 className="p-0">{activeTabLabel}</h2>
                  {activeTab === "1" && (
                    <CourseOverView overviewData={data?.overview} />
                  )}
                  {activeTab === "2" && (
                    <CourseSpStudyNotes
                      Link={data?.studyNotes}
                      course={data}
                      id={"spsn"}
                    />
                  )}
                  {activeTab === "3" && (
                    <CoursePracPaper
                      Link={data?.practicePapers}
                      course={data}
                      id={"pp"}
                    />
                  )}
                  {activeTab === "4" && (
                    <CourseQuestionBank
                      Link={data?.questionBank}
                      course={data}
                      id={"qb"}
                    />
                  )}
                  {/* {activeTab === "5" && (
                                    <CourseCombinedPackage Link={data?.combinedPackage} />
                                )} */}
                  {activeTab === "5" && (
                    <CourseReview
                      courseID={data?._id}
                      avgRatings={data?.ratings}
                    />
                  )}
                  {activeTab === "6" && <CourseFaq />}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <CourseBenefits />
      <ScrollTop />
    </>
  );
};

export default ParticularCourse;
